import { Hours } from '@obraplan/core/domain/shared/hours';
import type { HoursTotals } from '@obraplan/core/domain/reports/hours-report';
import { capacityUsePct, executionPct } from '@obraplan/core/domain/reports/indicators';
import type { FigureTone } from './Figure';

/**
 * Os cinco números do topo do painel, já escritos.
 *
 * O "realizado" traz sempre quantos dias ainda faltam conferir, e o percentual dele é
 * contra o planejado **dos mesmos dias**: sem isso, uma semana em curso pareceria uma
 * semana de baixa produção, quando o que falta é alguém olhar para ela.
 *
 * Cor só onde há o que dizer: saldo negativo, uso acima da capacidade e ociosidade que
 * passa de arredondamento. Verde em tudo o que vai bem é o mesmo que nenhuma cor.
 */
export interface KpiItem {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly tone?: FigureTone;
}

/** Abaixo disto, a ociosidade é arredondamento de calendário, não gente parada. */
const RELEVANT_IDLE_QUARTERS = 4 * 4;

function pct(value: number): string {
  return `${String(Math.round(value))}%`;
}

export function kpisFor(totals: HoursTotals): KpiItem[] {
  const hours = (quarters: number): string => Hours.ofQuarters(quarters).format();
  const pending = totals.totalCells - totals.inspectedCells;
  const use = capacityUsePct(totals);
  const execution = executionPct(totals);

  const executionHint =
    execution === undefined
      ? pending > 0
        ? `Nada conferido ainda · ${String(pending)} dias-recurso por conferir`
        : 'Nenhum dia-recurso planejado no período'
      : pending > 0
        ? `${pct(execution)} do planejado nos dias conferidos · ${String(pending)} por conferir`
        : `${pct(execution)} do planejado · tudo conferido`;

  return [
    {
      label: 'Disponíveis',
      value: hours(totals.availableQuarterHours),
      hint:
        totals.unlimitedCells > 0
          ? `${String(totals.unlimitedCells)} dias-recurso sem limite declarado ficam de fora`
          : 'Somadas dia a dia pelo calendário',
    },
    {
      label: 'Planejadas',
      value: hours(totals.plannedQuarterHours),
      hint:
        use === undefined
          ? 'O que foi lançado no planejamento'
          : use > 100
            ? `${pct(use)} da capacidade — acima do que a equipe tem`
            : `${pct(use)} da capacidade da equipe`,
      tone: use !== undefined && use > 100 ? 'danger' : 'default',
    },
    {
      label: 'Realizadas',
      value: hours(totals.executedQuarterHours),
      hint: executionHint,
    },
    {
      label: 'Saldo',
      value: hours(totals.balanceQuarterHours),
      hint:
        totals.balanceQuarterHours < 0
          ? 'Planejado além do disponível'
          : 'Disponível menos planejado',
      tone: totals.balanceQuarterHours < 0 ? 'danger' : 'default',
    },
    {
      label: 'Ociosidade',
      value: hours(totals.idleQuarterHours),
      hint:
        totals.overloadQuarterHours > 0
          ? `E ${hours(totals.overloadQuarterHours)} de sobrecarga em outros recursos`
          : 'Hora disponível que ninguém planejou',
      tone: totals.idleQuarterHours >= RELEVANT_IDLE_QUARTERS ? 'warn' : 'default',
    },
  ];
}
